import { useChartTitle, useReportId } from 'hooks/helperHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

import AllSessionsConfig from './AllSessionsConfig';
import AllSessionsSearchCriteria from './AllSessionsSearchCriteria';
import ChartSidebarSummary from 'components/Report/ReportShelf/ChartSidebarSummary';
import ReportBar from 'components/Report/ReportBar/ReportBar';
import ReportPage from 'components/Report/ReportPage/ReportPage';
import ReportShelf from 'components/Report/ReportShelf/ReportShelf';
import { Typography } from '@trackman/web-shared-components';
import UmpireAllSessionsHeader from 'components/Report/ReportPageHeader/UmpireAllSessionsHeader';
import { chartIdTochartSetup } from 'utilities/chartIdToComponent';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useTranslation } from 'react-i18next';
import useUmpireAllSessionsReport from 'hooks/UmpireReport/AllSessions/useUmpireAllSessionsReport';
import LoadingModal from 'components/Basic/LoadingModal/LoadingModal';
import { useChartVisibility } from 'hooks/useChartVisibility';
import { useChartBrokerStore } from 'stores/ChartBrokerStore';
import { displayDate } from 'utilities/dateHelpers';

const UmpireReportAllSessionsPage = () => {
  const { isCreate } = useReportId();

  const { t } = useTranslation();

  const {
    isEditMode,
    setIsEditMode,

    searchCriteria,
    searchChips,

    layout,
    tempLayout,
    chartData,

    isLoadingReport,
    generateReport,
    getNewData,
    setTempLayout,

    selectedConfgValues,
    specialConfigOptions,

    saveLayoutForSession,
    saveLayoutForOrg,

    resetStore,
    editingChart,
    setEditingChart,

    hasUnsavedEdits,
    discardLayoutChanges,
  } = useUmpireAllSessionsReport();

  const [openSection, setOpenSection] = useState<'searchCriteria' | 'config'>(isCreate ? 'searchCriteria' : 'config');
  const [parent] = useAutoAnimate();

  const downloadName = useMemo(() => {
    const { leagueNames = [], seasonNames = [], dateRanges = [], umpireNames = [] } = searchChips;
    const leagueStr = leagueNames.length === 1 ? leagueNames[0] : `${leagueNames.length} leagues`;
    const umpireStr = umpireNames.length === 1 ? umpireNames[0] : `${umpireNames.length} umpires`;
    const seasonStr = seasonNames.length === 0 ? '' : seasonNames.length === 1 ? seasonNames[0] : `${seasonNames.length} seasons`;
    const dateStr = dateRanges.length === 0 ? '' : `${displayDate(dateRanges[0].utcDateFrom)} - ${displayDate(dateRanges[0].utcDateTo)}`;
    return `Umpire Session Report - ${leagueStr} - ${umpireStr} - ${seasonStr || dateStr}`;
  }, [searchChips.leagueNames, searchChips.umpireNames, searchChips.seasonNames, searchChips.dateRanges]);

  const { reset: resetChartBrokerStore } = useChartBrokerStore();

  useEffect(() => {
    return () => {
      resetStore();
      resetChartBrokerStore();
    };
  }, [resetStore, resetChartBrokerStore]);

  const { getChartTitle, getChartSidebarSubTitle } = useChartTitle();

  const { visibleCharts, hiddenCharts, toggleChartVisibility } = useChartVisibility(tempLayout, setTempLayout);

  const handleGenerate = useCallback(() => {
    setOpenSection('config');
    generateReport();
  }, [generateReport]);

  const handleToggleVisibility = useCallback(
    (chartId: string) => {
      toggleChartVisibility(chartId);
    },
    [toggleChartVisibility]
  );

  return (
    <>
      <ReportBar
        reportName={t('UmpireReport.AllSessions')}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        downloadName={downloadName}
      />
      <div className='report-layout-content'>
        <ReportShelf
          onGenerate={handleGenerate}
          onModify={getNewData}
          isLoadingReport={isLoadingReport}
          disableGenerate={isLoadingReport || !searchCriteria.leagues || searchCriteria.leagues.length === 0}
          editingChart={editingChart}
          setEditingChart={setEditingChart}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          saveLayoutForSession={saveLayoutForSession}
          saveLayoutForOrg={saveLayoutForOrg}
          hasUnsavedEdits={hasUnsavedEdits}
          discardLayoutChanges={discardLayoutChanges}
        >
          <div className='report-layout-shelf' ref={parent}>
            {tempLayout && isEditMode ? (
              <>
                <div className='report-layout-shelf-section'>
                  {!editingChart && (
                    <Typography component='div' variant='body bold'>
                      {t('Report elements')}
                    </Typography>
                  )}
                  {visibleCharts.map((config) => {
                    const { chartId, chartDrawingInfo, ...chartConfig } = config;
                    const { Sidebar } = chartIdTochartSetup[chartId];
                    if (!editingChart) {
                      return (
                        <ChartSidebarSummary
                          key={config.chartId}
                          chartId={config.chartId}
                          onEditClick={Sidebar ? setEditingChart : undefined}
                          onToggleVisibility={handleToggleVisibility}
                          chartLabel={getChartTitle(config.chartId, config)}
                          subtitle={getChartSidebarSubTitle(config.chartId, config)}
                          isHidden={config.isHidden ?? false}
                        />
                      );
                    }
                    return Sidebar && editingChart === chartId ? (
                      <Sidebar chartConfig={chartConfig as never} setTempLayout={setTempLayout as any} tempLayout={tempLayout as any} />
                    ) : null;
                  })}
                </div>
                {!editingChart && hiddenCharts.length > 0 && (
                  <div className='report-layout-shelf-section'>
                    <Typography component='div' variant='body bold'>
                      {t('Extra elements')}
                    </Typography>
                    {hiddenCharts.map((config) => {
                      return (
                        <ChartSidebarSummary
                          key={config.chartId}
                          chartId={config.chartId}
                          onEditClick={undefined}
                          onToggleVisibility={handleToggleVisibility}
                          chartLabel={getChartTitle(config.chartId, config)}
                          subtitle={getChartSidebarSubTitle(config.chartId, config)}
                          isHidden={true}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <AllSessionsSearchCriteria
                  isSearchCriteriaOpen={openSection === 'searchCriteria'}
                  setSearchCriteriaOpen={() => setOpenSection('searchCriteria')}
                />
                <AllSessionsConfig isConfigOpen={openSection === 'config'} setConfigOpen={() => setOpenSection('config')} />
              </>
            )}
          </div>
        </ReportShelf>
        <div className='report-layout-grid-container'>
          {(chartData ?? []).map((chart, i) => (
            <ReportPage
              key={i}
              layout={layout}
              tempLayout={tempLayout}
              page={i + 1}
              header={
                <UmpireAllSessionsHeader
                  umpireName={chart.umpireName}
                  seasonNames={searchChips.seasonNames}
                  dateRanges={searchChips.dateRanges}
                  pitchTypes={
                    selectedConfgValues.pitchGrouping === 'Types'
                      ? selectedConfgValues.pitchTagging === 'Auto'
                        ? (specialConfigOptions?.pitchTypeInfo.availableAutoPitchTypes.filter((x) =>
                            selectedConfgValues.pitchTypes.includes(x)
                          ) ?? [])
                        : (specialConfigOptions?.pitchTypeInfo.availableManualPitchTypes.filter((x) =>
                            selectedConfgValues.pitchTypes.includes(x)
                          ) ?? [])
                      : undefined
                  }
                  pitchTypeGroups={
                    selectedConfgValues.pitchGrouping === 'Groups'
                      ? selectedConfgValues.pitchTagging === 'Auto'
                        ? (specialConfigOptions?.pitchTypeInfo.availableAutoPitchTypeGroups.filter((x) =>
                            selectedConfgValues.pitchTypeGroups.includes(x)
                          ) ?? [])
                        : (specialConfigOptions?.pitchTypeInfo.availableManualPitchTypeGroups.filter((x) =>
                            selectedConfgValues.pitchTypeGroups.includes(x)
                          ) ?? [])
                      : undefined
                  }
                  pitcherHandedness={selectedConfgValues.pitcherHandedness}
                  batterHandedness={selectedConfgValues.batterHandedness}
                  zoneViewMode={selectedConfgValues.strikeZoneDisplay}
                  callCorrectness={selectedConfgValues.includedCallCorrectness}
                  umpireCall={selectedConfgValues.includedCalls}
                />
              }
              pageChartData={chart}
              isEditMode={isEditMode}
              isLoadingData={isLoadingReport}
              setTempLayout={setTempLayout}
              setEditingChart={setEditingChart}
              editingChart={editingChart}
            />
          ))}
          {(chartData == null || chartData.length === 0) && (
            <ReportPage
              layout={layout}
              tempLayout={tempLayout}
              page={1}
              header={
                <UmpireAllSessionsHeader
                  pitchTypes={selectedConfgValues.pitchGrouping === 'Types' ? [] : undefined}
                  pitchTypeGroups={selectedConfgValues.pitchGrouping === 'Groups' ? [] : undefined}
                  pitcherHandedness={selectedConfgValues.pitcherHandedness}
                  batterHandedness={selectedConfgValues.batterHandedness}
                  zoneViewMode={selectedConfgValues.strikeZoneDisplay}
                  callCorrectness={selectedConfgValues.includedCallCorrectness}
                  umpireCall={selectedConfgValues.includedCalls}
                />
              }
              pageChartData={undefined}
              isEditMode={isEditMode}
              isLoadingData={isLoadingReport}
              setTempLayout={setTempLayout}
              setEditingChart={setEditingChart}
              editingChart={editingChart}
            />
          )}
        </div>
      </div>

      <LoadingModal
        isLoading={isLoadingReport}
        title={isCreate ? t('LoadingModal.title.creating') : t('LoadingModal.title.loading')}
        message={t('LoadingModal.message')}
      />
    </>
  );
};

export default UmpireReportAllSessionsPage;
